import classNames from 'classnames';
import React from 'react';
import { Row } from '../Row';
import PI from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useAuth } from '@/contexts';
import { getCountryCode } from '@/utils';
import { SupportedCountriesE } from '@/types/cyclone/models';

const ReactPhoneInput = PI;

const phoneExamples: Record<string, string> = {
  ar: '+54 11 1234 5678', // Argentina
  uy: '+598 91 234 567', // Uruguay
  cl: '+56 9 1234 5678', // Chile
  br: '+55 11 91234 5678', // Brasil
  py: '+595 981 123456', // Paraguay
  mx: '+52 55 1234 5678', // México
  ve: '+58 412 1234567', // Venezuela
  es: '+34 612 345 678', // España
  pe: '+51 912 345 678', // Perú
  co: '+57 312 345 6789', // Colombia
  us: '+1 212 555 1234', // Estados Unidos
  ca: '+1 416 555 1234', // Canadá
  gb: '+44 7123 456789', // Reino Unido
  fr: '+33 6 12 34 56 78', // Francia
  de: '+49 1512 3456789', // Alemania
  it: '+39 312 345 6789', // Italia
  pt: '+351 912 345 678', // Portugal
  nl: '+31 6 12345678', // Países Bajos
  be: '+32 470 12 34 56', // Bélgica
  ch: '+41 79 123 45 67', // Suiza
  se: '+46 70 123 45 67', // Suecia
  no: '+47 412 34 567', // Noruega
  fi: '+358 40 1234567', // Finlandia
  dk: '+45 20 12 34 56', // Dinamarca
  ie: '+353 85 123 4567' // Irlanda
};

const enableCountries = [
  'ar',
  'uy',
  'cl',
  'br',
  'py',
  'mx',
  've',
  'es',
  'pe',
  'co',
  'us',
  'ca',
  'gb',
  'fr',
  'de',
  'it',
  'pt',
  'nl',
  'be',
  'ch',
  'se',
  'no',
  'fi',
  'dk',
  'ie'
];

const customStyles = `
  .react-tel-input .flag-dropdown {
    background-color: #f0f0f0;
  }
  .react-tel-input .selected-flag .flag {
    display: none;
  }
  .react-tel-input .country-list .flag {
    display: none;
  }
  .react-tel-input .country-list .country {
    display: flex;
    align-items: center;
    padding: 7px 9px;
  }
  .react-tel-input .country-list .country-name {
    margin-left: 0;
  }
  .react-tel-input .selected-flag {
    padding-left: 4px;
  }
  .react-tel-input .selected-flag .arrow {
    display: none;
  }
  .react-tel-input .selected-flag {
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .react-tel-input .country-list {
    margin: 2px 0 0 -1px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  }
  .phone-example-text {
    font-size: 0.75rem;
    color: #6b7280;
    margin-top: 4px;
    margin-left: 4px;
  }
  .custom-caret-down {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  }
`;

type Props = {
  error?: boolean;
  id?: string;
  disabled?: boolean;
  inputPhoneProps?: Partial<React.ComponentProps<typeof ReactPhoneInput>>;
  callbackDropdownOpen?: (isOpen: boolean) => void;
  code?: string;
  showExample?: boolean;
  onChange: (formattedValue: string) => void;
};

export const InputPhone: React.FC<Props> = ({
  error = false,
  disabled,
  inputPhoneProps,
  id,
  callbackDropdownOpen,
  code,
  showExample = false,
  onChange
}) => {
  const { session } = useAuth();
  const phoneInputRef = React.useRef<HTMLDivElement | null>(null);
  const [isDropdownOpen, setDropdownOpen] = React.useState(false);
  const [currentCountry, setCurrentCountry] = React.useState(code || '');
  const [exampleText, setExampleText] = React.useState('');

  console.log(currentCountry);

  const countryCode = getCountryCode(session?.vendor?.country || SupportedCountriesE.ARGENTINA);

  React.useEffect(() => {
    const initialCountry = code || countryCode;
    setCurrentCountry(initialCountry);
    if (showExample) {
      setExampleText(`Ej: ${phoneExamples[initialCountry]}` || '');
    }
  }, [code, countryCode, showExample]);

  React.useEffect(() => {
    const handleDropdownClassChange = () => {
      const dropdown = phoneInputRef.current?.querySelector('.flag-dropdown');
      const isOpen = dropdown?.classList.contains('open') ?? false;
      setDropdownOpen(isOpen);

      if (phoneInputRef.current) {
        const selectedFlag = phoneInputRef.current.querySelector('.selected-flag');
        if (selectedFlag) {
          let caretElement = selectedFlag.querySelector('.custom-caret-down');

          if (!caretElement) {
            caretElement = document.createElement('div');
            caretElement.className = 'custom-caret-down';

            const svgString = `
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M7 10L12 15L17 10" stroke="#444" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            `;
            caretElement.innerHTML = svgString;

            selectedFlag.appendChild(caretElement);
          }
        }
      }
    };

    const observer = new MutationObserver(handleDropdownClassChange);
    if (phoneInputRef.current) {
      observer.observe(phoneInputRef.current, { childList: true, subtree: true });
      setTimeout(handleDropdownClassChange, 100);
    }
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (callbackDropdownOpen) callbackDropdownOpen(isDropdownOpen);
  }, [isDropdownOpen, callbackDropdownOpen]);

  React.useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = customStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  const handleCountryChange = (formattedValue: string, country: any) => {
    setCurrentCountry(country.countryCode);
    if (showExample) {
      setExampleText(`Ej: ${phoneExamples[country.countryCode]}` || '');
    }
    onChange(formattedValue);
  };

  return (
    <div className="phone-input-container">
      <Row
        align="center"
        ref={phoneInputRef}
        id={id}
        justify="space-between"
        gap={4}
        className={classNames({
          'w-full h-[40px] bg-clip-padding border border-solid border-[#0000005c] rounded transition ease-in-out m-0 focus:outline-none':
            true,
          'border-red-500': error,
          'cursor-not-allowed bg-gray-100': disabled,
          'bg-white focus-within:text-gray-700 focus-within:border-[#0072FB]': !disabled
        })}
      >
        <ReactPhoneInput
          disabled={disabled}
          {...inputPhoneProps}
          onChange={handleCountryChange}
          containerStyle={{ width: '100%', border: 'none', height: '38px' }}
          inputStyle={{
            width: '100%',
            border: 'none',
            height: '38px',
            paddingLeft: '52px'
          }}
          buttonStyle={{
            border: 'none',
            borderRight: '1px solid #0000005c',
            height: '38px',
            backgroundColor: '#f0f0f0',
            width: '42px'
          }}
          dropdownStyle={{
            width: '250px'
          }}
          onlyCountries={enableCountries}
          enableSearch={false}
          preferredCountries={['ar', 'uy', 'cl', 'br', 'py', 'mx', 've']}
          country={code ? code : countryCode}
        />
      </Row>

      {showExample && exampleText && <div className="phone-example-text">{exampleText}</div>}
    </div>
  );
};
